import { useMemo, useState } from 'react';

// material-ui
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

// project imports
import menuItems from '@/menu-items';
import { moderatorMenuItems } from '@/menu-items/moderator';
import { isNeighborhoodModerator, moderatorHomePath } from '@/providers/auth';

import { useGetMenuMaster } from '@/api/menu';
import {
	getViewableRoleIds,
	roleLabel,
	USER_ROLE_IDS
} from '@/hooks/fetch-hooks/users-api';
import { hasPermission } from '@/components/permission-gate';
import { useAuthContext } from '@/providers';
import { useAvailableRoles } from '@/hooks';
import { HORIZONTAL_MAX_ITEM, MenuOrientation } from '@/theme/config';
import useConfig from '@/theme/useConfig';

// types
import { NavItemType } from '@/types/menu';
import NavGroup from './NavGroup';
import NavItem from './NavItem';

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

// „Utilizatori web" — dropdown-ul din meniul lateral reflectă exact taburile de rol
// din pagina de utilizatori (Admin, …, Moderator), în funcție de rolul celui logat.
const withWebUsersRoles = (
	items: NavItemType[],
	viewerRole: string | undefined,
	availableRoles: { id: number; name: string; label?: string }[]
): NavItemType[] => {
	const viewableIds = getViewableRoleIds(
		viewerRole,
		availableRoles.map((role) => role.id)
	);
	const roleChildren = availableRoles
		// Moderatorii au secțiune dedicată (grupul Organizație) → nu mai apar în „Conturi interne".
		.filter(
			(role) => viewableIds.includes(role.id) && role.id !== USER_ROLE_IDS.moderator
		)
		.sort((a, b) => a.id - b.id)
		.map((role) => ({
			id: `acces-utilizatori-${role.id}`,
			title: roleLabel(role),
			type: 'item',
			url: `/common/users?roleId=${role.id}`
		}));

	// „Conturi interne" primește MEREU copiii calculați (chiar și lista goală): fără roluri
	// vizibile (ex. rolul de administrație), collapse-ul rămâne fără copii și e ascuns de
	// filterByPermission — altfel supraviețuia itemul static hardcodat „Administratori",
	// care trimitea la o pagină ce afișa „fără roluri".
	return items.map((group) =>
		group.children
			? {
					...group,
					children: group.children.map((child) =>
						child.id === 'acces-utilizatori'
							? { ...child, children: roleChildren }
							: child
					)
				}
			: group
	);
};

// Ascunde din meniu itemele pentru care utilizatorul nu are `requiredPermission`
// (aceeași logică ca <PermissionGate> de pe rute; superadmin le vede pe toate).
// Un părinte (group/collapse) rămas fără copii și fără url propriu e ascuns.
const filterByPermission = (
	items: NavItemType[],
	user: { role?: string; permissions?: string[] } | null | undefined
): NavItemType[] =>
	items.reduce<NavItemType[]>((acc, item) => {
		if (!hasPermission(user, item.requiredPermission, item.requiredPermissionMode))
			return acc;
		if (item.children) {
			const children = filterByPermission(item.children, user);
			if (children.length === 0 && !item.url) return acc;
			acc.push({ ...item, children });
		} else {
			acc.push(item);
		}
		return acc;
	}, []);

export default function Navigation() {
	const { state } = useConfig();
	const { menuMaster } = useGetMenuMaster();
	const { user } = useAuthContext();
	const availableRoles = useAvailableRoles();
	const drawerOpen = menuMaster.isDashboardDrawerOpened;
	const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

	const resolvedItems = useMemo(
		() =>
			isNeighborhoodModerator(user)
				? moderatorMenuItems(moderatorHomePath(user))
				: filterByPermission(
						withWebUsersRoles(menuItems.items, user?.role, availableRoles),
						user
					),
		[user, availableRoles]
	);

	const [selectedID, setSelectedID] = useState<string | undefined>('');
	const [selectedItems, setSelectedItems] = useState<string | undefined>('');
	const [selectedLevel, setSelectedLevel] = useState<number>(0);

	const isHorizontal = state.menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

	const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
	let lastItemIndex = resolvedItems.length - 1;
	let remItems: NavItemType[] = [];
	let lastItemId: string;

	if (lastItem && lastItem < resolvedItems.length) {
		lastItemId = resolvedItems[lastItem - 1].id!;
		lastItemIndex = lastItem - 1;
		remItems = resolvedItems.slice(lastItem - 1, resolvedItems.length).map((item) => ({
			title: item.title,
			elements: item.children,
			icon: item.icon,
			...(item.url && {
				url: item.url
			})
		}));
	}

	const navGroups = resolvedItems.slice(0, lastItemIndex + 1).map((item, index) => {
		switch (item.type) {
			case 'group':
				if (item.url && item.id !== lastItemId) {
					return (
						<List key={item.id} sx={{ zIndex: 0, ...(isHorizontal && { mt: 0.5 }) }}>
							{!isHorizontal && index !== 0 && <Divider sx={{ my: 0.5 }} />}
							<NavItem item={item} level={1} isParents setSelectedID={setSelectedID} />
						</List>
					);
				}
				return (
					<NavGroup
						key={item.id}
						setSelectedID={setSelectedID}
						setSelectedItems={setSelectedItems}
						setSelectedLevel={setSelectedLevel}
						selectedLevel={selectedLevel}
						selectedID={selectedID}
						selectedItems={selectedItems}
						lastItem={lastItem!}
						remItems={remItems}
						lastItemId={lastItemId}
						item={item}
					/>
				);
			default:
				return (
					<Typography
						key={item.id}
						variant="h6"
						sx={{ color: 'error.main', textAlign: 'center' }}
					>
						Fix - Navigation Group
					</Typography>
				);
		}
	});

	return (
		<Box
			sx={{
				pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
				...(!isHorizontal && { '& > ul:first-of-type': { mt: 0 } }),
				display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
			}}
		>
			{navGroups}
		</Box>
	);
}
